import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import axios from 'axios';
import {recordingConfirmPopupDisplay} from '../../../../../../../actions/popupActions';
import {API_ROOT} from '../../../../../../utils';
import PicButton from '../../../../../pic-button';
import Icon from '../../../../../icon';
import Button from '../../../../../button';

function ScanImage ({onClose}) {

	const MAX_FILE_SIZE_MB = 25;

	const [fileInfo, setFileInfo] = useState(null);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [imagePreview, setImagePreview] = useState(null);
	const [uploadComplete, setUploadComplete] = useState(false);
	const [error, setError] = useState(null);
	const [dragging, setDragging] = useState(false);
	const [fileToSend, setFileToSend] = useState(null);

	const fileInputRef = useRef(null);

	const selectedContactsGroup = useSelector(state => state.contactsGroupItemReducer.getSelectedContactsGroup);
	const profile = useSelector(state => state.profileReducer.getProfile);

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const uploadImage = useCallback(async (file) => {

		const formData = new FormData();
		formData.append("operation", "CREATE_CONTACT_FROM_IMAGE");
		formData.append("file", file, file.name);
		formData.append("fileType", "IMAGE");

		setUploadProgress(0);
		setUploadComplete(false);

		try {
			const res = await axios.post(API_ROOT + '/api/v1/file?source=WEBAPP', formData, {
				headers: { "Content-Type": "multipart/form-data" },
				withCredentials: true,
				onUploadProgress: (event) => {
					const percent = Math.round((event.loaded * 100) / event.total);
					setUploadProgress(percent);
				},
			});

			setFileToSend(res.data.fileId);
			setUploadComplete(true);
			console.log("Réponse API:", res.data);

		} catch (err) {
			console.error("Erreur upload:", err);
			setError("Échec de l’envoi du fichier.");
		}

	}, []);

	async function sendUploadedImage () {
		await handleContactOperation({
			labelId: selectedContactsGroup ? selectedContactsGroup.id : '6',
			labelOwnerId: selectedContactsGroup ? selectedContactsGroup.ownerId : profile.id,
			fileRefId: fileToSend,
		});

		console.log("fileRefId:", fileToSend);
	}

	const handleDrop = (e) => {
		e.preventDefault();
		setDragging(false);
		const file = e.dataTransfer.files[0];
		if (file) validateAndUpload(file);
	};

	const handleRemoveFile = useCallback((resetInput = true) => {
		setFileInfo(null);
		setUploadProgress(0);
		setImagePreview(null);
		setUploadComplete(false);
		setFileToSend(null);
		setError(null);

		if (resetInput && fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	}, []);

	const validateAndUpload = useCallback((file) => {
		if (!file.type.startsWith("image/")) {
			setError("Veuillez déposer une image valide (jpg, png, etc.)");
			return;
		}

		if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
			setError(`L'image ne doit pas dépasser ${MAX_FILE_SIZE_MB} Mo`);
			return;
		}

		handleRemoveFile(false);
		setError(null);
		setFileInfo(file);
		setImagePreview(URL.createObjectURL(file));
		uploadImage(file);
	}, [handleRemoveFile, uploadImage]);

	const handleClick = () => {
		fileInputRef.current.click();
	};

	const handleFileSelect = (e) => {
		const file = e.target.files[0];
		if (file) validateAndUpload(file);
	};

	const handleContactOperation = async payload => {

		try {
			await axios.post(API_ROOT + '/api/v2/contact/image', payload, {
				withCredentials: true
			});

			onClose();

			setTimeout(() => {
				dispatch(recordingConfirmPopupDisplay({ mode: 'create', fileRefId: payload.fileRefId }));
			}, 500)
		} catch (error) {
			console.error('Error creating contact:', error);
		}

	};

	useEffect(() => {
		const handlePaste = (e) => {
			const items = e.clipboardData.items;
			for (const item of items) {
				if (item.type.startsWith("image/")) {
					const file = item.getAsFile();
					if (file) validateAndUpload(file);
					break;
				}
			}
		};
		window.addEventListener("paste", handlePaste);
		return () => window.removeEventListener("paste", handlePaste);
	}, [validateAndUpload]);

	return (
		<div>
			{!fileInfo ? (
				<div
					onClick={handleClick}
					onDragOver={(e) => {
						e.preventDefault();
						setDragging(true);
					}}
					onDragLeave={() => setDragging(false)}
					onDrop={handleDrop}
					style={{
						border: "2px dashed #ccc",
						borderRadius: "10px",
						padding: "40px",
						textAlign: "center",
						background: dragging ? "#f0f8ff" : "#ffffff",
						cursor: "pointer",
						transition: "0.2s",
						marginBottom: "1em"
					}}
				>
					<div className="mb-3" style={{display: 'inline-block', padding: '.75em', borderRadius: '50%', backgroundColor: '#f5f5f5'}}>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M8.99994 17.75C8.58994 17.75 8.24994 17.41 8.24994 17V12.81L7.52994 13.53C7.23994 13.82 6.75994 13.82 6.46994 13.53C6.17994 13.24 6.17994 12.76 6.46994 12.47L8.46994 10.47C8.67994 10.26 9.00994 10.19 9.28994 10.31C9.56994 10.42 9.74994 10.7 9.74994 11V17C9.74994 17.41 9.40994 17.75 8.99994 17.75Z"
								fill="#2A72D6"/>
							<path
								d="M10.9999 13.7499C10.8099 13.7499 10.6199 13.6799 10.4699 13.5299L8.46994 11.5299C8.17994 11.2399 8.17994 10.7599 8.46994 10.4699C8.75994 10.1799 9.23994 10.1799 9.52994 10.4699L11.5299 12.4699C11.8199 12.7599 11.8199 13.2399 11.5299 13.5299C11.3799 13.6799 11.1899 13.7499 10.9999 13.7499Z"
								fill="#2A72D6"/>
							<path
								d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H14C14.41 1.25 14.75 1.59 14.75 2C14.75 2.41 14.41 2.75 14 2.75H9C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V10C21.25 9.59 21.59 9.25 22 9.25C22.41 9.25 22.75 9.59 22.75 10V15C22.75 20.43 20.43 22.75 15 22.75Z"
								fill="#2A72D6"/>
							<path
								d="M22 10.75H18C14.58 10.75 13.25 9.41999 13.25 5.99999V1.99999C13.25 1.69999 13.43 1.41999 13.71 1.30999C13.99 1.18999 14.31 1.25999 14.53 1.46999L22.53 9.46999C22.74 9.67999 22.81 10.01 22.69 10.29C22.57 10.57 22.3 10.75 22 10.75ZM14.75 3.80999V5.99999C14.75 8.57999 15.42 9.24999 18 9.24999H20.19L14.75 3.80999Z"
								fill="#2A72D6"/>
						</svg>
					</div>

					<p className="m-0">{t('scanImagePopupContent')}</p>
					<p className="m-0">(Max. File size: 25 MB)</p>
				</div>
			) : (
				<div
					style={{
						border: "1px solid #ddd",
						borderRadius: "12px",
						padding: "16px",
						gap: "12px",
						background: "#fff",
						transition: "0.3s",
						marginBottom: "1em"
					}}
				>
					<div style={{flexGrow: 1}}>
						<div className="d-flex">
							<div>
								{error && <p className="m-0 text-danger">Upload failed, please try again.</p>}
								<p style={{color: error ? '#dc3545' : '#666', margin: 0, fontWeight: 500}}>
									{fileInfo.name}
									{uploadComplete && (
										<svg className="ml-2" width="20" height="20" viewBox="0 0 20 20" fill="none"
										     xmlns="http://www.w3.org/2000/svg">
											<path
												d="M9.99984 1.6665C5.40817 1.6665 1.6665 5.40817 1.6665 9.99984C1.6665 14.5915 5.40817 18.3332 9.99984 18.3332C14.5915 18.3332 18.3332 14.5915 18.3332 9.99984C18.3332 5.40817 14.5915 1.6665 9.99984 1.6665ZM13.9832 8.08317L9.25817 12.8082C9.1415 12.9248 8.98317 12.9915 8.8165 12.9915C8.64984 12.9915 8.4915 12.9248 8.37484 12.8082L6.0165 10.4498C5.77484 10.2082 5.77484 9.80817 6.0165 9.5665C6.25817 9.32484 6.65817 9.32484 6.89984 9.5665L8.8165 11.4832L13.0998 7.19984C13.3415 6.95817 13.7415 6.95817 13.9832 7.19984C14.2248 7.4415 14.2248 7.83317 13.9832 8.08317Z"
												fill="#50C878"/>
										</svg>)}
								</p>
								<p style={{color: error ? '#dc3545' : '#666', margin: 0, fontSize: "0.85em"}}>
									{(fileInfo.size / 1024).toFixed(0)} KB
								</p>
							</div>
							<PicButton style={{height: 24, color: '#333'}} className="ml-auto" onClick={() => handleRemoveFile(true)}>
								<Icon name="trash-o"/>
							</PicButton>
						</div>

						<div className="d-flex">
							<div
								style={{
									width: "100%",
									height: "6px",
									background: "#f1f1f1",
									borderRadius: "4px",
									overflow: "hidden",
									marginTop: "8px",
								}}
							>
								<div
									style={{
										width: `${uploadProgress}%`,
										height: "100%",
										background: "#ff3b5f",
										transition: "width 0.2s ease",
									}}
								/>
							</div>
							<div className="ml-2">
                                <span style={{color: "#666", fontSize: "0.9em"}}>
                                    {uploadProgress}%
                                </span>
							</div>
						</div>
					</div>
					{imagePreview && (
						<div
							style={{
								marginTop: ".65em",
								textAlign: "center",
								borderRadius: "10px",
								overflow: "hidden",
							}}
						>
							<img
								src={imagePreview}
								alt="Prévisualisation"
								style={{
									maxWidth: "100%",
									maxHeight: "200px",
									objectFit: "contain",
									borderRadius: "8px",
									boxShadow: "0 0 4px rgba(0,0,0,0.1)",
									transition: "0.3s ease",
									opacity: uploadComplete ? 1 : 0.8,
								}}
							/>
						</div>
					)}
				</div>
			)}

			<div className="text-center">
				<Button
					onClick={() => sendUploadedImage()}
					disabled={fileToSend === null || error !== null || uploadComplete !== true}
					className="btn-primary"
				>
					Upload
				</Button>
			</div>

			<input
				type="file"
				ref={fileInputRef}
				onChange={handleFileSelect}
				accept="image/*"
				style={{display: "none"}}
			/>
		</div>
	)
}

export default ScanImage;
